import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  ShieldCheck,
  User,
  ArrowRight,
  AlertTriangle,
  Sparkles,
  Database,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const ROLES = [
  {
    id: "player",
    title: "Player",
    email: "player@spikesports.test",
    desc: "Regular member. Can browse, book, and see their booking history at /account.",
    icon: User,
    tone: "blue",
  },
  {
    id: "admin",
    title: "Admin",
    email: "admin@spikesports.test",
    desc: "Has access to /admin dashboard — confirm/cancel bookings, view leads & contacts.",
    icon: ShieldCheck,
    tone: "blue",
  },
];

const DevLogin = () => {
  const navigate = useNavigate();
  const { refresh, user, logout } = useAuth();
  const [enabled, setEnabled] = useState(null);
  const [working, setWorking] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dev/status");
        setEnabled(data.dev_mode);
      } catch {
        setEnabled(false);
      }
    })();
  }, []);

  const loginAs = async (role) => {
    setWorking(`login-${role}`);
    try {
      const { data } = await api.post(
        "/dev/login-as",
        { role },
        { withCredentials: true }
      );
      await refresh();
      toast.success(`Signed in as ${data.name}.`);
      navigate(data.is_admin ? "/admin" : "/account", { replace: true });
    } catch {
      toast.error("Could not sign in. Is DEV_MODE enabled?");
    } finally {
      setWorking("");
    }
  };

  const seedDemo = async () => {
    setWorking("seed");
    try {
      const { data } = await api.post("/dev/seed");
      if (data.seeded) {
        toast.success(`Seeded ${data.bookings} bookings, ${data.leads} lead, ${data.contacts} contact.`);
      } else {
        toast.info(`Already seeded (${data.existing_seed_bookings} bookings). Visit /admin to see them.`);
      }
    } catch {
      toast.error("Could not seed demo data.");
    } finally {
      setWorking("");
    }
  };

  if (enabled === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-spike-blue-light" />
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="max-w-md text-center bg-spike-elevated border border-amber-500/30 p-10">
          <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-5" />
          <h1 className="font-orbitron font-black uppercase text-2xl text-spike-text">
            Dev mode is off.
          </h1>
          <p className="mt-3 font-exo text-spike-text-muted text-sm">
            Set <code className="text-spike-blue-light">DEV_MODE=true</code> in <code className="text-spike-blue-light">/app/backend/.env</code> and restart the backend to enable this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-spike-surface min-h-screen" data-testid="dev-login-page">
      <section className="relative pt-16 pb-12 border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 stripe-pattern opacity-50" />
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-spike-blue/10 rounded-full blur-3xl" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="font-orbitron text-[0.7rem] uppercase tracking-[0.4em] text-spike-blue-light flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-spike-blue-light" /> Dev quick-login
          </div>
          <h1 className="mt-6 font-orbitron font-black uppercase text-4xl sm:text-5xl text-spike-text leading-tight">
            One-tap <span className="text-spike-blue-light">test access.</span>
          </h1>
          <p className="mt-4 font-exo text-spike-text-muted max-w-2xl">
            Skip Google OAuth. Pick a role and we'll seed the user, create a session,
            and drop the cookie. Use this for manual QA only — it's gated behind
            <code className="text-spike-blue-light mx-1">DEV_MODE</code>.
          </p>

          {user && (
            <div className="mt-6 inline-flex items-center gap-3 bg-spike-blue/10 border border-spike-blue/40 px-4 py-2">
              <div className="w-7 h-7 bg-spike-blue/20 flex items-center justify-center font-orbitron text-xs text-white">
                {user.name?.[0]?.toUpperCase()}
              </div>
              <span className="font-exo text-sm text-spike-text">
                Currently signed in as <span className="text-spike-text">{user.email}</span>
                {user.is_admin && <span className="ml-2 text-spike-blue-light">· admin</span>}
              </span>
              <button
                type="button"
                onClick={async () => {
                  await logout();
                  toast.success("Logged out.");
                }}
                data-testid="dev-logout-btn"
                className="ml-3 font-orbitron text-[0.65rem] uppercase tracking-[0.25em] text-spike-text-muted hover:text-spike-text"
              >
                Log out
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5" data-testid="dev-role-grid">
            {ROLES.map((r) => (
              <div
                key={r.id}
                data-testid={`dev-role-${r.id}`}
                className="bg-spike-elevated p-8 hover:bg-spike-elevated transition-colors"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 border border-spike-blue/40 flex items-center justify-center bg-spike-blue/5">
                    <r.icon className="w-4 h-4 text-spike-blue-light" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-orbitron font-bold uppercase tracking-wider text-spike-text">
                      {r.title}
                    </div>
                    <div className="font-orbitron text-[0.6rem] uppercase tracking-[0.3em] text-spike-blue-light mt-1">
                      {r.email}
                    </div>
                  </div>
                </div>
                <p className="font-exo text-sm text-spike-text-muted leading-relaxed min-h-[60px]">
                  {r.desc}
                </p>
                <button
                  type="button"
                  data-testid={`dev-login-${r.id}-btn`}
                  disabled={working === `login-${r.id}`}
                  onClick={() => loginAs(r.id)}
                  className="mt-6 inline-flex items-center gap-3 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-white font-orbitron font-bold uppercase tracking-[0.22em] text-xs px-6 py-4 transition-colors"
                >
                  {working === `login-${r.id}` ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Signing in...
                    </>
                  ) : (
                    <>
                      Login as {r.title} <ArrowRight className="w-3.5 h-3.5" />
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>

          {/* Seed data card */}
          <div className="mt-12 bg-spike-elevated border border-white/10 p-8" data-testid="dev-seed-card">
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-5 h-5 text-spike-blue-light" strokeWidth={1.5} />
              <h2 className="font-orbitron font-bold uppercase text-lg text-spike-text tracking-wider">
                Seed demo data
              </h2>
            </div>
            <p className="font-exo text-sm text-spike-text-muted leading-relaxed max-w-2xl">
              Creates 5 demo bookings (across all venues + statuses), 1 lead, and 1 contact message.
              Useful for populating the admin dashboard. Safe to re-run — won't duplicate unless you pass <code className="text-spike-blue-light">?force=true</code>.
            </p>
            <button
              type="button"
              data-testid="dev-seed-btn"
              disabled={working === "seed"}
              onClick={seedDemo}
              className="mt-6 inline-flex items-center gap-3 bg-transparent text-spike-text border border-white/10 hover:border-spike-blue hover:bg-spike-blue/10 font-orbitron font-bold uppercase tracking-[0.22em] text-xs px-6 py-4 transition-colors disabled:opacity-50"
            >
              {working === "seed" ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Seeding...
                </>
              ) : (
                <>
                  <Database className="w-3.5 h-3.5" /> Seed demo data
                </>
              )}
            </button>
          </div>

          {/* Test guide */}
          <div className="mt-12 bg-spike-elevated border border-white/10 p-8" data-testid="dev-flows-guide">
            <h2 className="font-orbitron font-bold uppercase text-lg text-spike-text tracking-wider mb-6">
              Flows to test
            </h2>
            <ol className="space-y-4 font-exo text-sm text-spike-text-muted list-decimal list-inside marker:text-spike-blue-light">
              <li><span className="text-spike-text">Public booking:</span> log out → go to <code className="text-spike-blue-light">/book</code> → pick venue, sport, date, time → submit → toast with booking ref.</li>
              <li><span className="text-spike-text">Lead magnet:</span> visit <code className="text-spike-blue-light">/membership</code> → click "Try a session — free" → submit → success screen with SPIKE-XXXXXX code.</li>
              <li><span className="text-spike-text">Member flow:</span> click "Login as Player" above → land on <code className="text-spike-blue-light">/account</code> → see seed bookings → book another at <code className="text-spike-blue-light">/book</code> → return to /account.</li>
              <li><span className="text-spike-text">Admin flow:</span> click "Login as Admin" → land on <code className="text-spike-blue-light">/admin</code> → switch between Bookings / Leads / Contacts tabs → confirm or cancel a booking.</li>
              <li><span className="text-spike-text">Availability:</span> at <code className="text-spike-blue-light">/book</code> pick Mississauga + a date → time grid colors slots green/amber/red → confirm slot capacity decreases after a booking.</li>
              <li><span className="text-spike-text">Coaches:</span> visit <code className="text-spike-blue-light">/coaches</code> → filter Cricket / Baseball → click "Book with X" → BookNets pre-fills coach + sport + venue.</li>
              <li><span className="text-spike-text">FAQ:</span> /membership FAQ uses shadcn Accordion — clicking expands/collapses.</li>
              <li><span className="text-spike-text">Rate limit:</span> spam /api/contact 6× from devtools — 6th returns 429.</li>
            </ol>
          </div>

          <div className="mt-10 text-center font-exo text-xs text-spike-dim">
            ⚠ This route is hidden from main navigation. Bookmark it as <code className="text-spike-blue-light">/dev-login</code>.
          </div>
        </div>
      </section>
    </div>
  );
};

export default DevLogin;
